<template>
  <div class="bcb-customer-detail container-fluid">
    <!-- Header -->
    <div class="d-flex align-items-center my-6">
      <button class="btn btn-icon btn-light mr-3" @click="goBack">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="flex-grow-1">
        <h4 class="text-dark fw-bold mb-1">Chi tiết khách hàng</h4>
        <p class="text-muted m-0">BCB Bundle Campaign</p>
      </div>
    </div>

    <b-overlay :show="loading">
      <div v-if="detail" class="row"> 
        <!-- Left: KH info + Timeline -->
        <div class="col-md-4">
          <!-- KH info -->
          <div class="bg-white rounded border p-5 mb-4">
            <div class="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
              <h6 class="font-weight-bold mb-0">Thông tin khách hàng</h6>
              <button v-if="isUpdatePermission" class="btn btn-outline-success btn-sm" @click="openEditCustomerModal">
                <i class="fas fa-edit mr-1"></i>Chỉnh sửa
              </button>
            </div>
            <div class="info-row"><span class="info-label">Họ tên:</span><span class="info-value font-weight-bold">{{ detail.fullName || '—' }}</span></div>
            <div class="info-row"><span class="info-label">SĐT:</span><span class="info-value">{{ detail.phone || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Email:</span><span class="info-value">{{ detail.email || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Công ty:</span><span class="info-value">{{ detail.companyName || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Phòng ban:</span><span class="info-value">{{ detail.department || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Giới tính:</span><span class="info-value">{{ genderLabel(detail.gender) }}</span></div>
            <div class="info-row"><span class="info-label">Ngày sinh:</span><span class="info-value">{{ formatDateStr(detail.dateOfBirth) }}</span></div>
            <div class="info-row"><span class="info-label">Ghi chú:</span><span class="info-value">{{ detail.noteImport || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Trạng thái:</span>
              <span class="status-badge" :style="statusStyle">{{ statusLabel }}</span>
            </div>
          </div>

          <!-- Status Timeline -->
          <div class="bg-white rounded border p-5">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">Timeline trạng thái</h6>
            <div class="timeline">
              <div
                v-for="(cfg, key) in customerStatusMap"
                :key="key"
                class="timeline-item"
                :class="{ 'timeline-item--active': Number(key) === detail.status, 'timeline-item--done': Number(key) < detail.status }"
              >
                <div class="timeline-dot"></div>
                <div class="timeline-content">
                  <span class="status-badge" :style="timelineBadgeStyle(cfg)">{{ key }}</span>
                  {{ cfg.label }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Sections -->
        <div class="col-md-8">
          <!-- Form đăng ký -->
          <div v-if="detail.registrationForm" class="bg-white rounded border p-5 mb-4">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">Form đăng ký của KH</h6>
            <div class="row">
              <div class="col-md-6 mb-3" v-for="(val, key) in detail.registrationForm" :key="key">
                <div class="info-label">{{ key }}</div>
                <div class="info-value">{{ val || '—' }}</div>
              </div>
            </div>
          </div>

          <!-- Thao tác -->
          <div class="bg-white rounded border p-5 mb-4">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">Thao tác</h6>
            <div class="d-flex flex-wrap gap-3">
              <button
                v-if="isUpdatePermission"
                class="btn btn-primary mr-3"
                :disabled="!canBookAppointment || actionLoading"
                @click="openBookModal"
              >
                <i class="fas fa-calendar-plus mr-2"></i>{{ bookBtnLabel }}
              </button>
              <button
                v-if="isUpdatePermission"
                class="btn btn-info mr-3"
                :disabled="!canUploadResult || actionLoading"
                @click="openUploadModal"
              >
                <i class="fas fa-upload mr-2"></i>Upload kết quả
              </button>
              <button
                v-if="isUpdatePermission"
                class="btn btn-primary mr-3 d-none"
                :disabled="true"
              >
                <b-spinner v-if="actionLoading === 'reminder'" small class="mr-2" />
                <i v-else class="fas fa-bell mr-2"></i>Gửi nhắc T-1
              </button>
              <button
                v-if="isUpdatePermission"
                class="btn btn-warning mr-3"
                :disabled="!canMarkExamCompleted || actionLoading"
                @click="markExamCompleted"
              >
                <b-spinner v-if="actionLoading === 'exam-completed'" small class="mr-2" />
                <i v-else class="fas fa-user-check mr-2"></i>Xác nhận đã khám
              </button>
              <button
                v-if="isUpdatePermission"
                class="btn btn-success"
                :disabled="!canSendResultNotification || actionLoading"
                @click="sendResultAvailableNotification"
              >
                <b-spinner v-if="actionLoading === 'result-notification'" small class="mr-2" />
                <i v-else class="fas fa-paper-plane mr-2"></i>Gửi TB kết quả
              </button>
            </div>
          </div>

          <!-- Kết quả khám -->
          <div class="bg-white rounded border p-5 mb-4">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">
              <i class="fas fa-file-medical mr-2 text-info"></i>Kết quả khám
            </h6>
            <div v-if="examResults.length" class="exam-result-list">
              <div
                v-for="(result, idx) in examResults"
                :key="result.id || idx"
                class="exam-result-item"
              >
                <div class="exam-result-item__index">{{ idx + 1 }}</div>
                <div class="exam-result-item__info">
                  <div class="exam-result-item__name">
                    <i class="fas fa-file-alt mr-1 text-primary"></i>
                    <span class="font-weight-bold">{{ result.additionalServices || '—' }}</span>
                  </div>
                  <div class="exam-result-item__meta">
                    <span class="exam-result-item__type">
                      <i class="fas fa-tag mr-1 text-warning"></i>
                      {{ examTypeLabel(result.type) }}
                    </span>
                    <span class="exam-result-item__date ml-3">
                      <i class="fas fa-calendar-upload mr-1 text-muted"></i>
                      {{ formatDateStr(result.uploadedAt) }}
                    </span>
                  </div>
                  <div v-if="result.saleNote" class="exam-result-item__note">
                    <i class="fas fa-sticky-note mr-1 text-muted"></i>{{ result.saleNote }}
                  </div>
                  <!-- Hiển thị persistent trạng thái OCR — trước đây chỉ báo qua toastr thoáng
                       qua lúc upload, ai không có mặt lúc đó sẽ không biết report tự động có
                       được tạo hay không (PO note: "silent ingest failure"). -->
                  <div v-if="result.ocrStatus === 'Failed'" class="exam-result-item__note text-danger">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    OCR không tự đọc được báo cáo sức khỏe từ file này — cần kiểm tra lại file
                    hoặc tạo báo cáo thủ công.
                  </div>
                  <div v-else-if="result.ocrStatus === 'Success'" class="exam-result-item__note text-success">
                    <i class="fas fa-check-circle mr-1"></i>Đã tự động tạo báo cáo sức khỏe.
                  </div>
                </div>
                <div class="ml-auto flex-shrink-0 d-flex align-items-center">
                  <span
                    v-if="result.ocrStatus === 'Failed'"
                    class="badge badge-danger mr-2"
                    title="OCR không đọc được file — chưa có báo cáo sức khỏe tự động"
                  >OCR lỗi</span>
                  <span
                    v-if="result.usage === 1"
                    class="badge badge-info mr-2"
                    title="Dùng để gửi email"
                  >Email</span>
                  <button
                    v-if="result.fileUrl"
                    class="btn btn-outline-primary btn-sm"
                    title="Xem file"
                    @click="openOriginalFile(result)"
                  >
                    <i class="fas fa-file-pdf"></i>
                  </button>
                  <button
                    class="btn btn-outline-danger btn-sm ml-2"
                    title="Xoá kết quả khám"
                    @click="confirmDeleteExamResult(result)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-muted text-center py-3">
              <i class="fas fa-folder-open mr-2"></i>Chưa có kết quả khám nào.
            </div>
          </div>

          <!-- Báo cáo sức khỏe tự động (OCR) — link ngược để không phải mò trong danh sách chung -->
          <div v-if="healthReports.length" class="bg-white rounded border p-5 mb-4">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">
              <i class="fas fa-notes-medical mr-2 text-success"></i>Báo cáo sức khỏe tự động ({{ healthReports.length }})
            </h6>
            <div
              v-for="report in healthReports"
              :key="report.id"
              class="d-flex align-items-center justify-content-between py-2"
            >
              <div>
                <span class="font-weight-bold">{{ report.patientName || '—' }}</span>
                <span class="badge badge-light ml-2">{{ report.state }}</span>
              </div>
              <router-link
                :to="{ name: 'health_report', query: { reportId: report.id } }"
                class="btn btn-outline-success btn-sm"
              >
                <i class="fas fa-eye mr-1"></i>Xem báo cáo
              </router-link>
            </div>
          </div>

          <!-- Lịch hẹn chính thức -->
          <div class="bg-white rounded border p-5 mb-4">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">
              <i class="fas fa-calendar-check mr-2 text-success"></i>Lịch hẹn chính thức
            </h6>
            <b-overlay :show="scheduleDaysLoading" rounded="sm">
              <div v-if="officialAppointments.length" class="wish-slot-list">
                <div
                  v-for="(appt, idx) in officialAppointments"
                  :key="appt.id || idx"
                  class="wish-slot-item wish-slot-item--official"
                >
                  <div class="wish-slot-item__index wish-slot-item__index--official">
                    <i class="fas fa-check"></i>
                  </div>
                  <div class="wish-slot-item__info">
                    <div v-if="appt.partnerName" class="wish-slot-item__partner">
                      <i class="fas fa-hospital mr-1 text-primary"></i><strong>{{ appt.partnerName }}</strong>
                    </div>
                    <div class="wish-slot-item__date">
                      <i class="fas fa-calendar-day mr-1 text-success"></i>
                      {{ formatDateStr(appt.examDate) || formatDateTime(appt.confirmedDatetime) }}
                    </div>
                    <div class="wish-slot-item__time">
                      <i class="fas fa-clock mr-1 text-success"></i>
                      <span v-if="appt.startTime">{{ formatTime(appt.startTime) }} — {{ formatTime(appt.endTime) }}</span>
                      <span v-else class="text-muted">Đặt lúc: {{ formatDateTime(appt.confirmedDatetime) }}</span>
                    </div>
                    <div v-if="appt.saleNote" class="wish-slot-item__note">
                      <i class="fas fa-sticky-note mr-1 text-muted"></i>{{ appt.saleNote }}
                    </div>
                  </div>
                  <div class="ml-auto d-flex align-items-center">
                    <b-badge variant="success">Chính thức</b-badge>
                    <button class="btn btn-icon btn-light btn-sm ml-2" @click="confirmDeleteAppointment(appt)" title="Xoá lịch hẹn">
                      <i class="fas fa-trash-alt text-danger"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted text-center py-3">
                <i class="fas fa-hourglass-half mr-2"></i>Chưa có lịch hẹn chính thức được xác nhận.
              </div>
            </b-overlay>
          </div>

          <!-- Lịch người dùng đăng ký -->
          <div v-if="false" class="bg-white rounded border p-5">
            <h6 class="font-weight-bold mb-4 border-bottom pb-3">
              <i class="fas fa-calendar-plus mr-2 text-primary"></i>Lịch người dùng đăng ký
            </h6>
            <div
              v-if="wishSlots && wishSlots.length"
              class="wish-slot-list"
            >
              <div
                v-for="(item, idx) in wishSlots"
                :key="idx"
                class="wish-slot-item"
              >
                <div class="wish-slot-item__index">{{ idx + 1 }}</div>
                <div class="wish-slot-item__info">
                  <div class="wish-slot-item__date">
                    <i class="fas fa-calendar-day mr-1 text-primary"></i>
                    {{ formatDateStr(item.examDate || item.date) }}
                  </div>
                  <div class="wish-slot-item__time">
                    <i class="fas fa-clock mr-1 text-info"></i>
                    {{ item.startTime || '—' }} — {{ item.endTime || '—' }}
                  </div>
                </div>
                <b-badge variant="primary" class="ml-auto">Mong muốn</b-badge>
              </div>
            </div>
            <div v-else class="text-muted text-center py-3">
              <i class="fas fa-calendar-times mr-2"></i>Khách hàng chưa đăng ký lịch mong muốn.
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <!-- Modal Upload Kết quả -->
    <b-modal
      v-model="showUploadModal"
      title="Upload kết quả khám"
      ok-title="Upload"
      cancel-title="Huỷ"
      ok-variant="success"
      size="lg"
      :ok-disabled="!uploadFileObj"
      @ok.prevent="submitUploadResult"
    >
      <b-overlay :show="uploadLoading">
        <div>
          <!-- Loại hoạt động -->
          <div class="form-group">
            <label class="font-weight-bold">Loại hoạt động <span class="text-danger">*</span></label>
            <b-form-select
              v-model="uploadType"
              :options="examTypeOptions"
              :state="uploadSubmitted && !uploadType ? false : null"
            />
            <b-form-invalid-feedback :state="uploadSubmitted && !uploadType ? false : null">
              Loại hoạt động là bắt buộc
            </b-form-invalid-feedback>
          </div>

          <!-- Tên file báo cáo -->
          <div class="form-group mt-3">
            <label class="font-weight-bold">Tên file báo cáo <span class="text-danger">*</span></label>
            <b-form-input
              v-model="uploadAdditionalServices"
              placeholder="Nhập tên file báo cáo"
              maxlength="200"
              :state="uploadSubmitted && !uploadAdditionalServices ? false : null"
            />
            <b-form-invalid-feedback :state="uploadSubmitted && !uploadAdditionalServices ? false : null">
              Tên file báo cáo là bắt buộc
            </b-form-invalid-feedback>
          </div>

          <!-- Chọn file -->
          <div class="form-group mt-3">
            <label class="font-weight-bold">Chọn file kết quả <span class="text-danger">*</span></label>
            <div class="wrap__file">
              <span class="file__name text-muted">
                {{ uploadFileObj ? uploadFileObj.name : 'Chưa chọn file...' }}
              </span>
              <b-icon-upload
                scale="1.5"
                class="cursor-pointer text-primary"
                @click="$refs.examFileUpload.click()"
              />
              <input
                v-show="false"
                type="file"
                ref="examFileUpload"
                accept=".pdf,.jpg,.png,.xlsx"
                @change="onUploadFile"
              />
            </div>
            <div class="text-danger small mt-1">
              <span v-if="uploadSubmitted && !uploadFileObj">File kết quả là bắt buộc</span>
              <span v-if="uploadFileSizeError">Kích thước file tối đa 100MB</span>
            </div>
          </div>

          <!-- Ghi chú -->
          <div class="form-group mt-3">
            <label class="font-weight-bold">Ghi chú</label>
            <b-form-textarea
              v-model="uploadNote"
              placeholder="Nhập ghi chú (tuỳ chọn)"
              rows="3"
            />
          </div>
        </div>
      </b-overlay>
    </b-modal>

    <!-- Modal Soát lại kết quả OCR — hiện ngay sau khi upload xong, để nhân viên upload tự xác
         nhận thông tin đọc tự động (tên/công ty/ngày khám) có khớp đúng file vừa chọn không,
         tránh trường hợp OCR đọc nhầm mà không ai biết cho tới khi bác sĩ review (PO note: "để
         user review"). Chỉ hiện khi OCR thành công — OCR fail đã có toastr cảnh báo riêng. -->
    <b-modal
      v-model="showOcrReviewModal"
      title="Soát lại thông tin đọc tự động"
      ok-title="Đúng, tiếp tục"
      cancel-title="Đóng"
      ok-only
      ok-variant="success"
      @ok="showOcrReviewModal = false"
    >
      <b-overlay :show="ocrReviewLoading">
        <div v-if="ocrReviewReport">
          <p class="text-muted mb-4">
            Hệ thống vừa tự động đọc file PDF và tạo báo cáo sức khỏe nháp. Vui lòng kiểm tra
            nhanh thông tin dưới đây có đúng với file vừa upload không — nếu sai, hãy báo cho
            Data Team/Admin để kiểm tra lại thủ công (chưa hỗ trợ tự sửa/xoá tại đây).
          </p>
          <table class="table table-borderless table-sm mb-0">
            <tbody>
              <tr>
                <td class="text-muted" style="width: 140px">Họ tên</td>
                <td class="font-weight-bold">{{ ocrReviewReport.patientName || '—' }}</td>
              </tr>
              <tr>
                <td class="text-muted">Công ty</td>
                <td>{{ ocrReviewReport.companyName || '—' }}</td>
              </tr>
              <tr>
                <td class="text-muted">Ngày khám</td>
                <td>{{ ocrReviewReport.examDate ? convertTimestampToDateNoTime(ocrReviewReport.examDate) : '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="ocrReviewReport.isFallbackTemplate" class="alert alert-warning mt-4 mb-0 py-2 px-3 small">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Không tự đọc được báo cáo sức khỏe từ file này (có thể sai mật khẩu hoặc sai định dạng
            PDF CarePlus). Số liệu chi tiết đang hiện là <b>dữ liệu mẫu</b>. Cần kiểm tra lại file
            hoặc tạo báo cáo thủ công.
          </div>
          <div v-else class="alert alert-success mt-4 mb-0 py-2 px-3 small">
            <i class="fas fa-check-circle mr-1"></i>
            Đã đọc được số liệu chi tiết (huyết áp, xét nghiệm...) từ file thật. Vui lòng vào báo
            cáo để bác sĩ review và bổ sung khuyến nghị trước khi gửi khách hàng.
          </div>
        </div>
        <div v-else class="text-center text-muted py-3">Đang tải thông tin báo cáo...</div>
      </b-overlay>
    </b-modal>

    <!-- Modal Đặt lịch / Đổi lịch -->
    <b-modal
      v-model="showBookModal"
      :title="bookBtnLabel"
      ok-title="Xác nhận đặt lịch"
      cancel-title="Huỷ"
      ok-variant="primary"
      size="lg"
      :ok-disabled="!bookSelectedSlotId || bookSubmitLoading"
      @ok.prevent="submitBookAppointment"
      @hidden="resetBookModal"
    >
      <b-overlay :show="bookScheduleDaysLoading || bookSubmitLoading">
        <!-- AccountId input nếu chưa có -->
        <div v-if="detail && !detail.accountId" class="form-group mb-4 p-3 border rounded bg-light">
          <label class="font-weight-bold">Account ID người dùng <span class="text-danger">*</span></label>
          <b-form-input
            v-model="bookAccountIdInput"
            placeholder="Nhập AccountId người dùng (GUID)"
          />
          <small class="text-muted">Khách hàng chưa có tài khoản liên kết. Nhập AccountId để tiếp tục.</small>
        </div>

        <!-- Chọn phòng khám -->
        <div class="form-group mb-4">
          <label class="font-weight-bold">Chọn phòng khám</label>
          <div v-if="bookPartnerOptions.length" class="partner-list">
            <div
              v-for="partner in bookPartnerOptions"
              :key="partner.id"
              class="partner-item"
              :class="{ 'partner-item--selected': bookSelectedPartnerId === partner.id }"
              @click="selectBookPartner(partner.id)"
            >
              <div class="d-flex align-items-center">
                <i class="fas fa-hospital mr-2 text-primary"></i>
                <span class="font-weight-bold">{{ partner.name }}</span>
              </div>
              <div v-if="partner.address" class="text-muted small mt-1">
                <i class="fas fa-map-marker-alt mr-1"></i>{{ partner.address }}
              </div>
              <div v-if="partner.hotline" class="text-muted small">
                <i class="fas fa-phone mr-1"></i>{{ partner.hotline }}
              </div>
            </div>
          </div>
          <div v-else class="text-muted small">Không có phòng khám nào cho chiến dịch này.</div>
        </div>

        <!-- Chọn ngày khám -->
        <div v-if="bookSelectedPartnerId" class="form-group mb-4">
          <label class="font-weight-bold">Chọn ngày khám</label>
          <div v-if="bookFilteredDays.length" class="day-cards-grid">
            <div
              v-for="day in bookFilteredDays"
              :key="day.examDate"
              class="day-card"
              :class="{
                'day-card--selected': bookSelectedDayDate === day.examDate,
                'day-card--inactive': day.slots.every(s => s.isFull || !s.isActive),
              }"
              @click="!day.slots.every(s => s.isFull || !s.isActive) && selectBookDay(day.examDate)"
            >
              <div class="day-card__date">{{ formatDateStr(day.examDate) }}</div>
              <div class="day-card__meta">
                {{ day.slots.reduce((sum, s) => sum + (s.bookedCount || 0), 0) }}/{{ day.slots.reduce((sum, s) => sum + (s.maxCapacity || 0), 0) }} đã đặt
              </div>
              <b-badge v-if="day.slots.every(s => s.isFull || !s.isActive)" variant="danger" class="day-card__badge">Đầy/Đóng</b-badge>
              <b-badge v-else variant="success" class="day-card__badge">Còn chỗ</b-badge>
            </div>
          </div>
          <div v-else class="text-muted small">Không có ngày khám nào.</div>
        </div>

        <!-- Chọn giờ khám -->
        <div v-if="bookSelectedDayDate" class="form-group mb-2">
          <label class="font-weight-bold">Chọn giờ khám</label>
          <div v-if="bookFilteredSlots.length" class="slot-list">
            <div
              v-for="slot in bookFilteredSlots"
              :key="slot.slotId"
              class="slot-item"
              :class="{
                'slot-item--selected': bookSelectedSlotId === slot.slotId,
                'slot-item--inactive': slot.isFull || !slot.isActive,
              }"
              @click="!slot.isFull && slot.isActive && selectBookSlot(slot.slotId)"
            >
              <div class="slot-item__check">
                <i v-if="bookSelectedSlotId === slot.slotId" class="fas fa-check-circle text-success"></i>
                <i v-else class="far fa-circle text-muted"></i>
              </div>
              <div class="slot-item__info">
                <div class="slot-item__time">{{ formatTime(slot.startTime) }} — {{ formatTime(slot.endTime) }}</div>
                <div class="slot-item__cap">{{ slot.bookedCount }}/{{ slot.maxCapacity }} đã đặt</div>
              </div>
              <b-badge v-if="slot.isFull || !slot.isActive" variant="danger">Đầy</b-badge>
              <b-badge v-else-if="bookSelectedSlotId === slot.slotId" variant="success">Đã chọn</b-badge>
            </div>
          </div>
          <div v-else class="text-muted small">Không có giờ khám nào trong ngày này.</div>
        </div>
      </b-overlay>
    </b-modal>

    <!-- Modal Chỉnh sửa thông tin khách hàng -->
    <b-modal
      v-model="showEditCustomerModal"
      title="Chỉnh sửa thông tin khách hàng"
      ok-title="Lưu"
      cancel-title="Huỷ"
      ok-variant="success"
      size="xl"
      :ok-disabled="editCustomerLoading"
      @ok="submitEditCustomer"
      @hidden="resetEditCustomerForm"
    >
      <b-overlay :show="editCustomerLoading" rounded="xl">
        <div class="row">
          <div class="col-md-4">
            <BasicInput
              label="Họ tên (*)"
              name="customerFullName"
              placeholder="Nhập họ tên"
              :value.sync="editCustomerForm.fullName"
            />
          </div>
          <div class="col-md-4">
            <BasicInput
              label="Số điện thoại (*)"
              name="customerPhone"
              placeholder="Nhập số điện thoại"
              :value.sync="editCustomerForm.phone"
            />
          </div>
          <div class="col-md-4">
            <BasicInput
              label="Email"
              name="customerEmail"
              placeholder="Nhập email"
              :value.sync="editCustomerForm.email"
            />
          </div>
          <div class="col-md-4">
            <BasicInput
              label="Công ty"
              name="customerCompanyName"
              placeholder="Nhập tên công ty"
              :value.sync="editCustomerForm.companyName"
            />
          </div>
          <div class="col-md-4">
            <BasicInput
              label="Phòng ban"
              name="customerDepartment"
              placeholder="Nhập phòng ban"
              :value.sync="editCustomerForm.department"
            />
          </div>
          <div class="col-md-4">
            <BasicSelect
              label="Giới tính"
              name="customerGender"
              :options="genderOptions"
              track-by="id"
              value-label="name"
              :allow-empty="false"
              :clear="false"
              :change-value-by-object="true"
              :value.sync="editCustomerForm.gender"
            />
          </div>
          <div class="col-md-4">
            <BasicDatePicker
              label="Ngày sinh"
              name="customerDateOfBirth"
              placeholder="Chọn ngày sinh"
              format="YYYY-MM-DD"
              value-type="format"
              :value.sync="editCustomerForm.dateOfBirth"
            />
          </div>
          <div class="col-md-8">
            <BasicTextArea
              label="Ghi chú"
              name="customerNoteImport"
              placeholder="Nhập ghi chú"
              :value.sync="editCustomerForm.noteImport"
            />
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCB_NOTIFICATION_METHOD,
  BCB_EXAM_RESULT_TYPE,
  BCB_EXAM_RESULT_TYPE_OPTIONS,
  CUSTOMER_STATUS,
  CUSTOMER_STATUS_WORKFLOW,
} from '../constants/index.js';
import { convertTimestampToDateNoTime } from '@/core/utils/dateUtils';

export default {
  name: 'BcbCustomerDetail',
  props: {
    campaignId: { type: [String, Number], default: null },
    customerId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      detail: null,
      healthReports: [],
      showOcrReviewModal: false,
      ocrReviewLoading: false,
      ocrReviewReport: null,
      showEditCustomerModal: false,
      editCustomerLoading: false,
      editCustomerForm: {
        fullName: '',
        phone: '',
        email: '',
        companyName: '',
        department: '',
        gender: null,
        dateOfBirth: '',
        noteImport: '',
      },
      genderOptions: [
        { id: 0, name: 'Nữ' },
        { id: 1, name: 'Nam' },
        { id: 2, name: 'Khác' },
      ],
      showUploadModal: false,
      actionLoading: null,
      slotsLoading: false,
      scheduleDaysLoading: false,
      scheduleDays: [],
      // Upload result fields
      uploadFileObj: null,
      uploadNote: '',
      uploadType: null,
      uploadAdditionalServices: '',
      uploadLoading: false,
      uploadSubmitted: false,
      uploadFileSizeError: false,
      customerStatusMap: CUSTOMER_STATUS_WORKFLOW,
      examTypeOptions: BCB_EXAM_RESULT_TYPE_OPTIONS,
      appointmentsWithPartner: [],
      // Book appointment modal state
      showBookModal: false,
      bookPartners: [],
      bookScheduleDaysLoading: false,
      bookSelectedPartnerId: null,
      bookSelectedDayDate: null,
      bookSelectedSlotId: null,
      bookAccountIdInput: '',
      bookSubmitLoading: false,
    };
  },
  computed: {
    resolvedCampaignId() {
      return this.campaignId || this.$route.params.campaignId;
    },
    resolvedCustomerId() {
      return this.customerId || this.$route.params.customerId;
    },
    statusLabel() {
      return CUSTOMER_STATUS[this.detail?.status]?.label || '—';
    },
    statusStyle() {
      const cfg = CUSTOMER_STATUS[this.detail?.status];
      return {
        backgroundColor: cfg?.bg || '#F1EFE8',
        color: cfg?.color || '#444441',
      };
    },
    examResults() {
      return this.detail?.examResult ?? this.detail?.examResults ?? [];
    },
    canMarkExamCompleted() {
      return [6, 7].includes(Number(this.detail?.status));
    },
    canUploadResult() {
      return Number(this.detail?.status) >= 8;
    },
    canSendResultNotification() {
      return Number(this.detail?.status) >= 8 && this.examResults.length > 0;
    },
    canBookAppointment() {
      const status = Number(this.detail?.status);
      return status >= 1 && status <= 7;
    },
    hasExistingAppointment() {
      return this.officialAppointments.length > 0;
    },
    bookBtnLabel() {
      return this.hasExistingAppointment ? 'Đổi lịch' : 'Đặt lịch';
    },
    bookPartnerOptions() {
      return this.bookPartners.map((p) => ({
        id: p.partnerId,
        name: p.partnerName,
        address: p.partnerAddress,
        hotline: p.partnerHotline,
      }));
    },
    bookFilteredDays() {
      if (!this.bookSelectedPartnerId) return [];
      const partner = this.bookPartners.find((p) => p.partnerId === this.bookSelectedPartnerId);
      if (!partner) return [];
      const dayMap = {};
      (partner.slots || []).forEach((slot) => {
        const date = slot.examDate;
        if (!dayMap[date]) dayMap[date] = { examDate: date, slots: [] };
        dayMap[date].slots.push(slot);
      });
      return Object.values(dayMap).sort((a, b) => a.examDate - b.examDate);
    },
    bookFilteredSlots() {
      if (!this.bookSelectedDayDate) return [];
      const day = this.bookFilteredDays.find((d) => d.examDate === this.bookSelectedDayDate);
      return day?.slots ?? [];
    },
    sortedPreferredSlots() {
      const slots = this.detail?.preferredSlots ?? [];
      return [...slots].sort((a, b) => {
        const timeA = a.date ? (typeof a.date === 'number' ? a.date * 1000 : new Date(a.date).getTime()) : 0;
        const timeB = b.date ? (typeof b.date === 'number' ? b.date * 1000 : new Date(b.date).getTime()) : 0;
        return timeA - timeB;
      });
    },
    // Flatten tất cả slots từ tất cả ngày, kèm thông tin ngày
    allSlots() {
      return this.scheduleDays.flatMap((day) =>
        (day.slots ?? []).map((slot) => ({
          ...slot,
          examDate: day.examDate,
          dayId: day.id,
        }))
      );
    },
    // Lịch người dùng mong muốn đăng ký — map slotId với scheduleDays
    wishSlots() {
      const wishes = this.detail?.appointmentWish ?? [];
      if (!wishes.length) return [];
      const mappedWishes = [...wishes].map((w) => {
        const slot = this.allSlots.find((s) => s.id === w.slotId);
        return {
          ...w,
          examDate: slot?.examDate ?? null,
          startTime: slot?.startTime ?? null,
          endTime: slot?.endTime ?? null,
        };
      });
      return mappedWishes.sort((a, b) => {
        const timeA = a.examDate ? (typeof a.examDate === 'number' ? a.examDate * 1000 : new Date(a.examDate).getTime()) : 0;
        const timeB = b.examDate ? (typeof b.examDate === 'number' ? b.examDate * 1000 : new Date(b.examDate).getTime()) : 0;
        return timeA - timeB;
      });
    },
    // Lịch hẹn chính thức người dùng đã đặt — dùng API with-partner nếu có
    officialAppointments() {
      const appts = this.appointmentsWithPartner.length
        ? this.appointmentsWithPartner
        : (this.detail?.appointment ?? []).map((appt) => {
            const slot = appt.slotId ? this.allSlots.find((s) => s.id === appt.slotId) : null;
            return {
              ...appt,
              examDate: appt.examDate ?? slot?.examDate ?? null,
              startTime: appt.startTime ?? slot?.startTime ?? null,
              endTime: appt.endTime ?? slot?.endTime ?? null,
            };
          });
      return appts.sort((a, b) => {
        const dateA = a.examDate || a.confirmedDatetime;
        const dateB = b.examDate || b.confirmedDatetime;
        const timeA = dateA ? (typeof dateA === 'number' ? dateA * 1000 : new Date(dateA).getTime()) : 0;
        const timeB = dateB ? (typeof dateB === 'number' ? dateB * 1000 : new Date(dateB).getTime()) : 0;
        return timeA - timeB;
      });
    },
  },
  async created() {
    await this.loadDetail();
    await this.loadScheduleDays();
    this.loadHealthReports();
  },
  methods: {
    convertTimestampToDateNoTime,
    async loadDetail() {
      this.loading = true;
      try {
        const res = await this.$api.get(`BcbCampaignCustomer/${this.resolvedCustomerId}`);
        this.detail = res?.data ?? res;
        const apptRes = await this.$api.get('BcbCustomerAppointment/with-partner', {
          params: { campaignCustomerId: this.resolvedCustomerId },
        });
        const apptData = apptRes?.data ?? apptRes;
        this.appointmentsWithPartner = Array.isArray(apptData) ? apptData : (apptData?.data ?? []);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tải thông tin khách hàng.' });
      } finally {
        this.loading = false;
      }
    },
    // PO note: trang "Báo cáo sức khỏe" hiện TẤT CẢ report, không lọc theo khách hàng — thêm link
    // ngược ở đây để nhân viên/bác sĩ mở đúng report của khách này mà không phải mò trong danh sách chung.
    async loadHealthReports() {
      try {
        const res = await this.$api.get('HealthReport', {
          params: { campaignCustomerId: this.resolvedCustomerId, size: 50 },
        });
        const data = res?.data ?? res;
        this.healthReports = data?.items ?? [];
      } catch (e) {
        // im lặng — đây là thông tin bổ sung, không chặn trang chi tiết khách hàng nếu lỗi
        this.healthReports = [];
      }
    },
    openEditCustomerModal() {
      if (!this.detail) return;
      this.editCustomerForm = {
        fullName: this.detail.fullName || '',
        phone: this.detail.phone || '',
        email: this.detail.email || '',
        companyName: this.detail.companyName || '',
        department: this.detail.department || '',
        gender: this.genderOptions.find((item) => item.id === Number(this.detail.gender)) || this.genderOptions[0],
        dateOfBirth: this.toDatePickerValue(this.detail.dateOfBirth),
        noteImport: this.detail.noteImport || '',
      };
      this.showEditCustomerModal = true;
    },
    resetEditCustomerForm() {
      this.editCustomerLoading = false;
    },
    toDatePickerValue(value) {
      if (!value) return '';
      if (typeof value === 'number') {
        return this.$moment.unix(value).utcOffset('+07:00').format('YYYY-MM-DD');
      }
      return this.$moment(value).utcOffset('+07:00').format('YYYY-MM-DD');
    },
    buildFormData(obj) {
      const formData = new FormData();
      Object.entries(obj).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      return formData;
    },
    validateEditCustomerForm() {
      if (!this.editCustomerForm.fullName || !this.editCustomerForm.fullName.trim()) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Họ tên khách hàng là bắt buộc.' });
        return false;
      }

      if (!this.editCustomerForm.phone || !this.editCustomerForm.phone.trim()) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Số điện thoại là bắt buộc.' });
        return false;
      }

      return true;
    },
    async submitEditCustomer(bvModalEvent) {
      if (bvModalEvent && bvModalEvent.preventDefault) {
        bvModalEvent.preventDefault();
      }

      if (!this.validateEditCustomerForm()) return;

      const payload = {
        fullName: this.editCustomerForm.fullName.trim(),
        phone: this.editCustomerForm.phone.trim(),
        email: this.editCustomerForm.email ? this.editCustomerForm.email.trim() : '',
        companyName: this.editCustomerForm.companyName || '',
        department: this.editCustomerForm.department || '',
        gender: Number(this.editCustomerForm.gender?.id ?? this.editCustomerForm.gender ?? 0),
        noteImport: this.editCustomerForm.noteImport || '',
        status: this.detail?.status || 0,
        accountId: this.detail?.accountId || null,
      };

      if (this.editCustomerForm.dateOfBirth) {
        const [year, month, day] = this.editCustomerForm.dateOfBirth.split('-').map(Number);
        payload.dateOfBirth = Date.UTC(year, month - 1, day) / 1000;
      }

      this.editCustomerLoading = true;
      try {
        await this.$api.put(`BcbCampaignCustomer/${this.resolvedCustomerId}`, this.buildFormData(payload));
        this.$toastr.s({ title: 'Thành công', msg: 'Đã cập nhật thông tin khách hàng.' });
        this.showEditCustomerModal = false;
        await this.loadDetail();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể cập nhật thông tin khách hàng.' });
      } finally {
        this.editCustomerLoading = false;
      }
    },
    async loadScheduleDays() {
      this.scheduleDaysLoading = true;
      try {
        const params = this.detail?.partnerId
          ? { partnerId: this.detail.partnerId }
          : { campaignId: this.resolvedCampaignId };
        const res = await this.$api.get('BcbPartnerScheduleDay', { params });
        const raw = res?.data ?? res;
        const days = Array.isArray(raw) ? raw : (raw?.data ?? []);
        this.scheduleDays = days.sort((a, b) => {
          const timeA = a.examDate ? (typeof a.examDate === 'number' ? a.examDate * 1000 : new Date(a.examDate).getTime()) : 0;
          const timeB = b.examDate ? (typeof b.examDate === 'number' ? b.examDate * 1000 : new Date(b.examDate).getTime()) : 0;
          return timeA - timeB;
        }).map(day => {
          day.totalRegisteredCount = 0;
          if (day.slots && Array.isArray(day.slots)) {
            day.slots.sort((s1, s2) => {
              if (!s1.startTime || !s2.startTime) return 0;
              return s1.startTime.localeCompare(s2.startTime);
            });
            day.totalRegisteredCount = day.slots.reduce((sum, slot) => sum + (slot.registeredCount || 0), 0);
          }
          return day;
        });
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải danh sách lịch khám.' });
      } finally {
        this.scheduleDaysLoading = false;
      }
    },
    openOriginalFile(result) {
      if (!result?.fileUrl) return;
      window.open(result.fileUrl, '_blank');
    },
    async confirmDeleteExamResult(result) {
      if (!result?.id) return;

      const confirm = await this.$swal({
        title: 'Xác nhận xoá',
        text: 'Bạn có chắc chắn muốn xoá kết quả khám này không?',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Huỷ',
            value: false,
            visible: true,
            className: '',
            closeModal: true,
          },
          confirm: {
            text: 'Xoá',
            value: true,
            visible: true,
            className: 'btn-danger',
            closeModal: true,
          },
        },
      });

      if (confirm) {
        await this.deleteExamResult(result.id);
      }
    },
    async deleteExamResult(id) {
      try {
        await this.$api.delete(`BcbExamResult/${id}`);
        this.$toastr.s({ title: 'Thành công', msg: 'Đã xoá kết quả khám.' });
        await this.loadDetail();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể xoá kết quả khám.' });
      }
    },
    async confirmDeleteAppointment(appt) {
      let confirm = await this.$swal({
        title: 'Xác nhận xoá',
        text: 'Bạn có chắc chắn muốn xoá lịch hẹn chính thức này không?',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Huỷ',
            value: false,
            visible: true,
            className: '',
            closeModal: true,
          },
          confirm: {
            text: 'Xoá',
            value: true,
            visible: true,
            className: 'btn-danger',
            closeModal: true,
          },
        },
      });
      if (confirm) {
        await this.deleteAppointment(appt.id);
      }
    },
    async deleteAppointment(id) {
      try {
        await this.$api.delete(`BcbCustomerAppointment/${id}`);
        this.$toastr.s({ title: 'Thành công', msg: 'Đã xoá lịch hẹn chính thức.' });
        if (this.detail?.appointment) {
          this.detail.appointment = this.detail.appointment.filter((item) => item.id !== id);
        }
        await Promise.all([
          this.loadDetail(),
          this.loadScheduleDays(),
        ]);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể xoá lịch hẹn.' });
      }
    },
    async confirmAction(text) {
      return this.$swal({
        title: 'Xác nhận',
        text,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: '',
            closeModal: true,
          },
          confirm: {
            text: 'Xác nhận',
            value: true,
            visible: true,
            className: 'btn-success',
            closeModal: true,
          },
        },
      });
    },
    buildNotificationFormData() {
      const formData = new FormData();
      formData.append('campaignCustomerId', this.resolvedCustomerId);
      formData.append('notificationMethod', BCB_NOTIFICATION_METHOD.AUTO);
      return formData;
    },
    async markExamCompleted() {
      if (!this.canMarkExamCompleted) return;
      const confirmed = await this.confirmAction('Xác nhận khách hàng đã hoàn thành buổi khám?');
      if (!confirmed) return;

      this.actionLoading = 'exam-completed';
      try {
        await this.$api.put(`BcbCampaignCustomer/${this.resolvedCustomerId}/exam-completed`);
        this.$toastr.s({ title: 'Thanh cong', msg: 'Đã cập nhật trạng thái đã khám.' });
        await this.loadDetail();
      } catch (e) {
        this.$toastr.e({ title: 'Loi', msg: e?.message || 'Không thể xác nhận đã khám.' });
      } finally {
        this.actionLoading = null;
      }
    },
    async sendResultAvailableNotification() {
      if (!this.canSendResultNotification) return;
      const confirmed = await this.confirmAction('Gửi thông báo có kết quả cho khách hàng này?');
      if (!confirmed) return;

      this.actionLoading = 'result-notification';
      try {
        await this.$api.post('BcbCampaignNotification/SendResultAvailableNotification', this.buildNotificationFormData());
        this.$toastr.s({ title: 'Thanh cong', msg: 'Đã gửi thông báo có kết quả.' });
        await this.loadDetail();
      } catch (e) {
        this.$toastr.e({ title: 'Loi', msg: e?.message || 'Không thể gửi thông báo có kết quả.' });
      } finally {
        this.actionLoading = null;
      }
    },
    openUploadModal() {
      this.uploadFileObj = null;
      this.uploadNote = '';
      this.uploadType = null;
      this.uploadAdditionalServices = '';
      this.uploadSubmitted = false;
      this.uploadFileSizeError = false;
      this.showUploadModal = true;
    },
    onUploadFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploadFileSizeError = false;
      if (file.size > 1024 * 1024 * 100) {
        this.uploadFileSizeError = true;
        this.$refs.examFileUpload.value = null;
        return;
      }
      this.uploadFileObj = file;
    },
    async uploadFileToServer(file) {
      const fileToUpload = file || this.uploadFileObj;
      try {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        const data = await this.$api.post('/File/BCB', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data.data;
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Upload file thất bại.' });
        return null;
      }
    },
    formatDobToPassword(dob) {
      if (!dob) return null;
      let m;
      if (typeof dob === 'number') {
        m = this.$moment.unix(dob).utcOffset('+07:00');
      } else {
        m = this.$moment(dob).utcOffset('+07:00');
      }
      if (!m.isValid()) return null;
      return m.format('DDMMYYYY');
    },
    async protectPdf(file, password) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('password', password);
        const response = await this.$api.post('/File/protect-pdf', formData, {
          responseType: 'blob',
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 phút cho PDF protection
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const protectedName = file.name.replace(/\.pdf$/i, '') + '_protected.pdf';
        return new File([blob], protectedName, { type: 'application/pdf' });
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Protect PDF thất bại.' });
        return null;
      }
    },
    // PO note: "Upload thành công" chỉ xác nhận file đã lưu, KHÔNG xác nhận báo cáo sức khỏe đã
    // được tạo tự động (OCR có thể fail do sai password/format PDF...) — phải báo rõ, tránh nhân
    // viên tưởng xong việc trong khi bác sĩ không hề có data.
    notifyOcrStatus(ocrStatus) {
      if (ocrStatus === 'Failed') {
        this.$toastr.w({
          title: 'Lưu ý',
          msg: 'Không tự đọc được báo cáo sức khỏe từ file này (có thể sai mật khẩu hoặc sai định dạng PDF CarePlus). Cần kiểm tra lại file hoặc tạo báo cáo thủ công.',
        });
      } else if (ocrStatus === 'Success') {
        this.$toastr.s({ title: 'Thành công', msg: 'Đã tự động tạo báo cáo sức khỏe từ file vừa upload.' });
      }
      // ocrStatus null/undefined = loại hoạt động không áp dụng OCR (không phải "Kết quả khám sức khỏe") — im lặng.
    },
    async saveExamResult(fileUrl, usage) {
      const formData = new FormData();
      formData.append('campaignCustomerId', this.resolvedCustomerId);
      formData.append('fileUrl', fileUrl);
      formData.append('additionalServices', this.uploadAdditionalServices);
      formData.append('saleNote', this.uploadNote || '');
      formData.append('type', this.uploadType);
      formData.append('usage', usage);
      const res = await this.$api.post('BcbExamResult', formData);
      return {
        error: res && res.error,
        ocrStatus: res && res.data && res.data.ocrStatus,
        healthReportId: res && res.data && res.data.healthReportId,
      };
    },
    // PO note: "để user review" — sau khi OCR thành công, nhân viên upload xem ngay thông tin
    // đọc tự động (không phải chờ bác sĩ mở /health-report mới biết OCR đọc đúng hay sai).
    async openOcrReviewModal(healthReportId) {
      if (!healthReportId) return;
      this.ocrReviewReport = null;
      this.showOcrReviewModal = true;
      this.ocrReviewLoading = true;
      try {
        const res = await this.$api.get(`HealthReport/${healthReportId}`);
        this.ocrReviewReport = res?.data ?? res;
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được thông tin báo cáo vừa tạo.' });
        this.showOcrReviewModal = false;
      } finally {
        this.ocrReviewLoading = false;
      }
    },
    async submitUploadResult() {
      this.uploadSubmitted = true;
      if (!this.uploadType || !this.uploadAdditionalServices || !this.uploadFileObj || this.uploadFileSizeError) {
        return;
      }
      try {
        this.uploadLoading = true;

        // Hỏi người dùng có muốn gửi qua email không
        const sendViaEmail = await this.$swal({
          title: 'Gửi qua email?',
          text: 'Bạn có muốn tạo bản sao có mật khẩu để gửi email cho khách hàng không?',
          icon: 'question',
          buttons: {
            cancel: {
              text: 'Không',
              value: false,
              visible: true,
              className: '',
              closeModal: true,
            },
            confirm: {
              text: 'Có',
              value: true,
              visible: true,
              className: 'btn-primary',
              closeModal: true,
            },
          },
        });

        if (sendViaEmail) {
          const password = this.formatDobToPassword(this.detail?.dateOfBirth);
          if (!password) {
            this.$toastr.e({ title: 'Lỗi', msg: 'Không tìm thấy ngày sinh của khách hàng để tạo mật khẩu.' });
            return;
          }

          // Bảo vệ PDF với password
          const protectedFile = await this.protectPdf(this.uploadFileObj, password);
          if (!protectedFile) return;

          // Upload cả 2 file song song
          const [originalUrl, protectedUrl] = await Promise.all([
            this.uploadFileToServer(this.uploadFileObj),
            this.uploadFileToServer(protectedFile),
          ]);
          if (!originalUrl || !protectedUrl) return;

          // Lưu 2 records: usage=0 (app), usage=1 (email)
          const res1 = await this.saveExamResult(originalUrl, 0);
          if (res1.error) return;
          const res2 = await this.saveExamResult(protectedUrl, 1);
          if (res2.error) return;
          this.notifyOcrStatus(res1.ocrStatus);
          this.showUploadModal = false;
          if (res1.ocrStatus === 'Success') await this.openOcrReviewModal(res1.healthReportId);
        } else {
          const fileUrl = await this.uploadFileToServer();
          if (!fileUrl) return;

          const res = await this.saveExamResult(fileUrl, 0);
          if (res.error) return;
          this.notifyOcrStatus(res.ocrStatus);
          this.showUploadModal = false;
          if (res.ocrStatus === 'Success') await this.openOcrReviewModal(res.healthReportId);
        }

        this.$toastr.s({ title: 'Thành công', msg: 'Đã upload kết quả khám.' });
        this.loadDetail();
        this.loadHealthReports();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Upload thất bại.' });
      } finally {
        this.uploadLoading = false;
      }
    },
    async openBookModal() {
      this.resetBookModal();
      this.showBookModal = true;
      this.bookScheduleDaysLoading = true;
      try {
        const res = await this.$api.get('BcbPartnerScheduleDay/BcBCampaignId', {
          params: { bcbCampaignId: this.resolvedCampaignId,phoneNumber: this.detail?.phone },
        });
        const raw = res?.data ?? res;
        this.bookPartners = Array.isArray(raw) ? raw : (raw?.data ?? []);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải danh sách phòng khám.' });
      } finally {
        this.bookScheduleDaysLoading = false;
      }
    },
    resetBookModal() {
      this.bookPartners = [];
      this.bookSelectedPartnerId = null;
      this.bookSelectedDayDate = null;
      this.bookSelectedSlotId = null;
      this.bookAccountIdInput = '';
    },
    selectBookPartner(partnerId) {
      this.bookSelectedPartnerId = partnerId;
      this.bookSelectedDayDate = null;
      this.bookSelectedSlotId = null;
    },
    selectBookDay(examDate) {
      this.bookSelectedDayDate = examDate;
      this.bookSelectedSlotId = null;
    },
    selectBookSlot(slotId) {
      this.bookSelectedSlotId = slotId;
    },
    async submitBookAppointment() {
      if (!this.detail.accountId && !this.bookAccountIdInput.trim()) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng nhập AccountId người dùng.' });
        return;
      }
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!this.detail.accountId && !guidRegex.test(this.bookAccountIdInput.trim())) {
        this.$toastr.e({ title: 'Lỗi', msg: 'AccountId không hợp lệ. Phải là định dạng GUID.' });
        return;
      }
      if (this.hasExistingAppointment) {
        const confirmed = await this.$swal({
          title: 'Xác nhận đổi lịch',
          text: 'Khách hàng đã có lịch hẹn. Bạn có chắc muốn cập nhật sang lịch mới không?',
          icon: 'warning',
          buttons: {
            cancel: { text: 'Huỷ', value: false, visible: true, className: '', closeModal: true },
            confirm: { text: 'Đổi lịch', value: true, visible: true, className: 'btn-primary', closeModal: true },
          },
        });
        if (!confirmed) return;
      }
      this.bookSubmitLoading = true;
      try {
        if (!this.detail.accountId && this.bookAccountIdInput.trim()) {
          const payload = {
            fullName: this.detail.fullName || '',
            phone: this.detail.phone || '',
            email: this.detail.email || '',
            companyName: this.detail.companyName || '',
            department: this.detail.department || '',
            gender: Number(this.detail.gender ?? 0),
            noteImport: this.detail.noteImport || '',
            status: this.detail.status || 0,
            accountId: this.bookAccountIdInput.trim(),
          };
          if (this.detail.dateOfBirth) payload.dateOfBirth = this.detail.dateOfBirth;
          await this.$api.put(`BcbCampaignCustomer/${this.resolvedCustomerId}`, this.buildFormData(payload));
        }
        if (this.hasExistingAppointment) {
          const existingId = this.officialAppointments[0].id;
          await this.$api.put(`BcbCustomerAppointment/${existingId}/reschedule`, {
            slotId: this.bookSelectedSlotId,
            phoneNumber: this.detail.phone || '',
          });
        } else {
          const formData = new FormData();
          formData.append('slotId', this.bookSelectedSlotId);
          formData.append('campaignCustomerId', this.resolvedCustomerId);
          formData.append('phoneNumber', this.detail.phone || '');
          await this.$api.post('BcbCustomerAppointment', formData);
        }
        this.$toastr.s({ title: 'Thành công', msg: 'Đã đặt lịch thành công.' });
        this.showBookModal = false;
        await Promise.all([this.loadDetail(), this.loadScheduleDays()]);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể đặt lịch. Vui lòng thử lại.' });
      } finally {
        this.bookSubmitLoading = false;
      }
    },
    timelineBadgeStyle(cfg) {
      return {
        backgroundColor: cfg?.bg || '#F1EFE8',
        color: cfg?.color || '#444441',
      };
    },
    goBack() {
      const cid = this.resolvedCampaignId;
      if (cid && cid !== 'unknown') {
        this.$router.push({ name: 'bcb-campaign-detail', params: { id: cid } });
      } else if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: 'bcb-campaign-customer-list' });
      }
    },
    examTypeLabel(type) {
      return BCB_EXAM_RESULT_TYPE[type] || '—';
    },
    genderLabel(value) {
      const option = this.genderOptions.find((item) => item.id === Number(value));
      return option?.name || '—';
    },
    formatDate(val) {
      if (!val) return '—';
      return this.$moment.unix(val).utcOffset('+07:00').format('DD/MM/YYYY');
    },
    // Hỗ trợ cả ISO string lẫn unix timestamp — chỉ ngày
    formatDateStr(val) {
      if (!val) return '—';
      if (typeof val === 'number') {
        return this.$moment.unix(val).utcOffset('+07:00').format('DD/MM/YYYY');
      }
      return this.$moment(val).utcOffset('+07:00').format('DD/MM/YYYY');
    },
    // Format datetime đầy đủ (ngày + giờ)
    formatDateTime(val) {
      if (!val) return '—';
      if (typeof val === 'number') {
        return this.$moment.unix(val).utcOffset('+07:00').format('DD/MM/YYYY HH:mm');
      }
      return this.$moment(val).utcOffset('+07:00').format('DD/MM/YYYY HH:mm');
    },
    formatTime(val) {
      if (!val) return '—';
      if (typeof val === 'string') {
        return val.slice(0, 5);
      }
      return val;
    },
  },
};
</script>

<style scoped lang="scss">
.info-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.88rem;
  margin-bottom: 8px;
}
.info-label {
  color: #7e8299;
  min-width: 90px;
  flex-shrink: 0;
  font-size: 0.82rem;
}
.info-value {
  color: #181c32;
  word-break: break-word;
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.timeline-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-left: 2px solid #e4e6ef;
  padding-left: 16px;
  position: relative;
  color: #a1a5b7;

  &--done {
    color: #50cd89;
    border-left-color: #50cd89;
    .timeline-dot { background: #50cd89; }
  }
  &--active {
    color: #181c32;
    font-weight: 700;
    border-left-color: #009ef7;
    .timeline-dot { background: #009ef7; }
  }
}
.timeline-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e4e6ef;
  position: absolute;
  left: -6px;
}
.timeline-content {
  font-size: 0.88rem;
}

/* ===== Day Cards ===== */
.day-cards-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.day-card {
  border: 2px solid #e4e6ef;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  min-width: 130px;
  text-align: center;
  transition: all 0.18s ease;
  background: #fff;
  position: relative;

  &:hover:not(.day-card--inactive) {
    border-color: #009ef7;
    box-shadow: 0 0 0 3px rgba(0, 158, 247, 0.12);
  }
  &--selected {
    border-color: #009ef7 !important;
    background: #f0f8ff;
    box-shadow: 0 0 0 3px rgba(0, 158, 247, 0.18);
  }
  &--inactive {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__date {
    font-weight: 700;
    font-size: 1rem;
    color: #181c32;
    margin-bottom: 4px;
  }
  &__meta {
    font-size: 0.78rem;
    color: #7e8299;
    margin-bottom: 6px;
  }
  &__badge {
    font-size: 0.72rem;
  }
}

/* ===== Slot List ===== */
.slot-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.slot-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid #e4e6ef;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  background: #fff;

  &:hover:not(.slot-item--inactive) {
    border-color: #50cd89;
    background: #f6fffb;
  }
  &--selected {
    border-color: #50cd89 !important;
    background: #f0fff6;
    box-shadow: 0 0 0 3px rgba(80, 205, 137, 0.15);
  }
  &--inactive {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__check {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
    font-size: 0.88rem;
  }
  &__time {
    font-weight: 600;
    color: #181c32;
  }
  &__cap {
    color: #7e8299;
  }
}

/* ===== Wish Slot List ===== */
.wish-slot-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wish-slot-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid #e4e6ef;
  border-radius: 8px;
  padding: 10px 14px;
  background: #f9f9ff;
  transition: border-color 0.18s;

  &:hover {
    border-color: #009ef7;
  }

  &__index {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #009ef7;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--official {
      background: #50cd89;
    }
  }

  &--official {
    background: #f0fff8;
    border-color: #d3f5e5;

    &:hover {
      border-color: #50cd89;
    }
  }

  &__info {
    flex: 1;
  }

  &__date {
    font-weight: 600;
    font-size: 0.9rem;
    color: #181c32;
    margin-bottom: 2px;
  }

  &__time {
    font-size: 0.82rem;
    color: #7e8299;
  }

  &__note {
    font-size: 0.78rem;
    color: #a1a5b7;
    margin-top: 4px;
  }
}

/* ===== Partner List ===== */
.partner-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.partner-item {
  border: 2px solid #e4e6ef;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  background: #fff;

  &:hover {
    border-color: #009ef7;
    background: #f0f8ff;
  }
  &--selected {
    border-color: #009ef7 !important;
    background: #f0f8ff;
    box-shadow: 0 0 0 3px rgba(0, 158, 247, 0.18);
  }
}

/* ===== Upload File Picker ===== */
.wrap__file {
  padding: 10px 16px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
}
.file__name {
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
}

/* ===== Exam Result List ===== */
.exam-result-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.exam-result-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 12px 14px;
  background: #f9fbff;
  transition: border-color 0.18s;

  &:hover {
    border-color: #009ef7;
  }

  &__index {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #009ef7;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 0.9rem;
    color: #181c32;
    margin-bottom: 4px;
  }

  &__meta {
    font-size: 0.8rem;
    color: #7e8299;
    margin-bottom: 4px;
  }

  &__type {
    font-weight: 600;
    color: #f39c12;
  }

  &__date {
    color: #a1a5b7;
  }

  &__note {
    font-size: 0.78rem;
    color: #a1a5b7;
    margin-top: 2px;
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
</style>
